import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useCustomSelectors } from '../../services/selectors/allSelectors';
import { subscribeRealtimeEvent } from '../../services/realtime/realtimeSocket';
import { showBrowserNotification } from '../../utils/miscellaneous/browserNotification';
import * as images from '../../assets/images/index';
import {
	FLOW_ASSIGNMENT_SOCKET_EVENT,
	FLOW_CHANGED_EVENT,
} from '../../screens/dashboard/notifications/components/flowEvents';

// Listens (Socket.IO) for OATRx Flow steps assigned to the logged-in user on any page:
// shows a toast + browser notification and tells open Flow panels to refresh their Daily list.

const capitalizeName = (name) => (name || '').replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

const FlowAssignmentToast = ({ assigner, title }) => (
	<div className="mobile-request-toast-content" style={{ borderLeftColor: '#2563eb' }}>
		<div className="mobile-request-toast-icon mobile-request-toast-icon--prescription">
			<span />
		</div>
		<div className="mobile-request-toast-copy">
			<p className="mobile-request-toast-title">New OATRx Flow task</p>
			<p className="mobile-request-toast-description" style={{ color: '#17212f', fontWeight: 600 }}>{title}</p>
			<p className="mobile-request-toast-description">Assigned by {assigner}</p>
		</div>
	</div>
);

const FlowAssignmentNotifier = () => {
	const { pharmacyId, loginUserID } = useCustomSelectors();
	const seenEventIdsRef = useRef(new Set());

	useEffect(() => {
		if (!pharmacyId || !loginUserID) return undefined;

		const subscription = subscribeRealtimeEvent(FLOW_ASSIGNMENT_SOCKET_EVENT, (message) => {
			const payload = Array.isArray(message?.data) ? message.data[0] : message?.data;
			if (!payload) return;

			// Published to the whole pharmacy room; only react to events addressed to this user.
			const userIds = (payload.user_ids || []).map(Number);
			if (Number(payload.pharmacy_id) !== Number(pharmacyId) || !userIds.includes(Number(loginUserID))) return;

			if (payload.event_id) {
				if (seenEventIdsRef.current.has(payload.event_id)) return;
				seenEventIdsRef.current.add(payload.event_id);
			}

			window.dispatchEvent(new CustomEvent(FLOW_CHANGED_EVENT, { detail: payload }));

			if (payload.action !== 'assigned') return;

			const assigner = capitalizeName(payload.assigned_by?.name) || 'Your manager';
			const title = payload.step?.title || 'a new step';
			// Same card style as the other realtime toasts (RealtimeDemoManager / mobile-request-toast).
			toast.info(<FlowAssignmentToast assigner={assigner} title={title} />, {
				autoClose: 6000,
				className: 'mobile-request-toast',
				bodyClassName: 'mobile-request-toast-body',
				icon: false,
			});
			showBrowserNotification('New OATRx Flow step', {
				body: `${assigner} assigned you: ${title}`,
				icon: images.oatrx_logo,
			});
		});

		return () => subscription?.close?.();
	}, [pharmacyId, loginUserID]);

	return null;
};

export default FlowAssignmentNotifier;
